"use client";
import { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { MdPerson } from "react-icons/md";
import { AxiosResponse } from "axios";
import type toast from "react-hot-toast";
import ModalIconTitle from "../ModalIconTitle";
import { useModalWidthClass } from "../../hooks/useModalWidth";
import { BackendApi } from "../../utils/BackendApi";
import { ApiRoute } from "../../utils/ApiRoute";
import { SystemMsg } from "../../utils/SystemMsg";
import common from "../../utils/common";
import { Role } from "../../class/prisma/db";
import {
  CreateUserRequest,
  UpdateUserRequest,
} from "../../class/user.schema";
import { useI18n } from "../../i18n";
export type UserFormData = {
  id: number;
  username: string;
  role: string;
};

type UserModalProps = {
  toast: typeof toast;
  mode: "add" | "edit";
  show: boolean;
  data?: UserFormData;
  onSaved: () => void;
  onClose: () => void;
};

function apiErrorMessage(error: unknown, fallback: string) {
  if (!error || typeof error !== "object") {
    return fallback;
  }
  const responseData = (
    error as { response?: { data?: { message?: unknown } }; data?: { message?: unknown } }
  ).response?.data ?? (error as { data?: { message?: unknown } }).data;
  const message = responseData?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

export default function UserModal({
  toast,
  mode,
  show,
  data,
  onSaved,
  onClose,
}: UserModalProps) {
  const { t } = useI18n();
  const dialogClassName = useModalWidthClass();
  const isAddMode = mode === "add";
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<Role>(Role.USER);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [usernameInvalid, setUsernameInvalid] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [passwordInvalidMsg, setPasswordInvalidMsg] = useState(
    SystemMsg.User.passwordEmpty,
  );
  const [confirmInvalid, setConfirmInvalid] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }
    setUsername(isAddMode ? "" : (data?.username ?? ""));
    setRole(data?.role === Role.ADMIN ? Role.ADMIN : Role.USER);
    setPassword("");
    setPasswordConfirm("");
    setUsernameInvalid(false);
    setPasswordInvalid(false);
    setPasswordInvalidMsg(SystemMsg.User.passwordEmpty);
    setConfirmInvalid(false);
    setSaving(false);
  }, [show, isAddMode, data]);

  const onHide = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const saveClick = () => {
    const trimmedUsername = username.trim();
    const usernameEmpty = common.stringIsNullOrEmpty(trimmedUsername);
    const passwordEmpty = common.stringIsNullOrEmpty(password);
    const confirmEmpty = common.stringIsNullOrEmpty(passwordConfirm);
    const passwordRequired = isAddMode;
    const passwordProvided = !passwordEmpty || !confirmEmpty;
    let nextPasswordInvalid = false;
    let nextPasswordMsg = SystemMsg.User.passwordEmpty;
    let nextConfirmInvalid = false;

    if (passwordRequired && passwordEmpty) {
      nextPasswordInvalid = true;
    }
    if (passwordProvided && password !== passwordConfirm) {
      nextPasswordInvalid = true;
      nextConfirmInvalid = true;
      nextPasswordMsg = SystemMsg.User.passwordMismatch;
    }
    if (passwordProvided && confirmEmpty) {
      nextConfirmInvalid = true;
    }

    setUsernameInvalid(usernameEmpty);
    setPasswordInvalid(nextPasswordInvalid);
    setPasswordInvalidMsg(nextPasswordMsg);
    setConfirmInvalid(nextConfirmInvalid);

    if (usernameEmpty) {
      toast.error(SystemMsg.User.usernameEmpty);
      return;
    }
    if (nextPasswordInvalid || nextConfirmInvalid) {
      toast.error(
        nextPasswordMsg === SystemMsg.User.passwordMismatch
          ? SystemMsg.User.passwordMismatch
          : SystemMsg.User.passwordEmpty,
      );
      return;
    }

    const succFunc = (resp: AxiosResponse) => {
      setSaving(false);
      if (resp.status !== 200) {
        toast.error(
          isAddMode ? SystemMsg.User.createFailed : SystemMsg.User.updateFailed,
        );
        return;
      }
      toast.success(
        isAddMode ? SystemMsg.User.createSuccess : SystemMsg.User.updateSuccess,
      );
      onSaved();
      onHide();
    };
    const errFunc = (error: AxiosResponse | string) => {
      setSaving(false);
      toast.error(
        apiErrorMessage(
          error,
          isAddMode ? SystemMsg.User.createFailed : SystemMsg.User.updateFailed,
        ),
      );
    };

    setSaving(true);
    if (isAddMode) {
      const body: CreateUserRequest = {
        username: trimmedUsername,
        password,
        role,
      };
      BackendApi.post(ApiRoute.user, body, succFunc, errFunc);
      return;
    }

    const body: UpdateUserRequest = {
      username: trimmedUsername,
      role,
    };
    if (!passwordEmpty) {
      body.password = password;
    }
    BackendApi.put(`${ApiRoute.user}/${data?.id}`, body, succFunc, errFunc);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      dialogClassName={dialogClassName}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <ModalIconTitle
            title={isAddMode ? t("user.addUser") : t("user.editUser")}
            iconElement={<MdPerson />}
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="userModal.username">
            <Form.Label>{t("fields.username")}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t("fields.username")}
              value={username}
              isInvalid={usernameInvalid}
              onChange={(event) => {
                setUsername(event.target.value);
                if (event.target.value.trim().length > 0) {
                  setUsernameInvalid(false);
                }
              }}
            />
            <Form.Control.Feedback type="invalid">
              {SystemMsg.User.usernameEmpty}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="userModal.role">
            <Form.Label>{t("fields.role")}</Form.Label>
            <Form.Select
              value={role}
              onChange={(event) => {
                setRole(
                  event.target.value === Role.ADMIN ? Role.ADMIN : Role.USER,
                );
              }}
            >
              <option value={Role.USER}>USER</option>
              <option value={Role.ADMIN}>ADMIN</option>
            </Form.Select>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="userModal.password">
            <Form.Label>
              {isAddMode ? t("fields.password") : t("fields.newPassword")}
            </Form.Label>
            <Form.Control
              type="password"
              placeholder={
                isAddMode ? t("fields.password") : t("settings.keepCurrentPassword")
              }
              value={password}
              isInvalid={passwordInvalid}
              onChange={(event) => {
                setPassword(event.target.value);
                setPasswordInvalid(false);
              }}
            />
            <Form.Control.Feedback type="invalid">
              {passwordInvalidMsg}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="userModal.passwordConfirm">
            <Form.Label>{t("fields.confirmPassword")}</Form.Label>
            <Form.Control
              type="password"
              placeholder={t("fields.confirmPassword")}
              value={passwordConfirm}
              isInvalid={confirmInvalid}
              onChange={(event) => {
                setPasswordConfirm(event.target.value);
                setConfirmInvalid(false);
              }}
            />
            <Form.Control.Feedback type="invalid">
              {passwordInvalidMsg}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={saveClick} disabled={saving}>
          {t("common.save")}
        </Button>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          {t("common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
